import React, { useEffect } from "react";
import "./App.css";

import {
  BrowserRouter as Router,
  Route,
  Navigate,
  Routes,
  useParams,
} from "react-router-dom";

import { createAuthLink, AuthOptions } from "aws-appsync-auth-link";
import { createSubscriptionHandshakeLink } from "aws-appsync-subscription-link";
import {
  ApolloLink,
  ApolloClient,
  ApolloProvider,
  InMemoryCache,
} from "@apollo/client";

import { QueryClient, QueryClientProvider } from "@tanstack/react-query";

import appSyncConfig from "../aws-exports";
import Footer from "../Components/HeaderFooter/Footer";
import Navbar from "../Components/HeaderFooter/Navbar";
import {
  PrivacyPolicy,
  TermsOfService,
  CopyrightPolicy,
} from "../Features/Legalities";
import UserProfileLoading from "../Features/UserProfile/UserProfileLoading";
import ExpandedPlaylistLoading from "../Features/ExpandedPlaylist/ExpandedPlaylistLoading";
import Sandbox from "./Sandbox";
import ExpandedPostLoading from "../Features/ExpandedPost/ExpandedPostLoading";
import { Main } from "../Features/HomePage/Main";
// import { HomePageV2 } from "../Features/HomePageV2/HomePageV2";

import { DesktopCenteringView } from "../Components/RecurringSmallViews/RecurringSmallViews";
import { HomePageForCreators } from "../Features/HomePage_ForCreators/HomePage_ForCreators";
import { HomePageForListeners } from "../Features/HomePage_ForListeners/HomePage_ForListeners";
import { FakeablePasswordSplashScreen } from "../Features/YouTubeLikeDemo/FakePasswordSplashScreen";
import { YouTubeLikeDemo } from "../Features/YouTubeLikeDemo/YouTubeLikeDemo";
import {
  lexDemoPodcasts,
  sanjayPodcasts,
} from "../Features/YouTubeLikeDemo/PodcastSelectionInterface";

const url = appSyncConfig.aws_appsync_graphqlEndpoint;
const region = appSyncConfig.aws_appsync_region;
const auth: AuthOptions = {
  type: "API_KEY",
  apiKey: appSyncConfig.aws_appsync_apiKey,
};

const link = ApolloLink.from([
  createAuthLink({ url, region, auth }),
  createSubscriptionHandshakeLink({ url, region, auth }),
]);

const gqlClient = new ApolloClient({
  link,
  cache: new InMemoryCache(),
});

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 5,
    },
  },
});

function _WithHeaderFooter(inside: JSX.Element): JSX.Element {
  return (
    <div>
      <Navbar />

      {DesktopCenteringView(inside)}

      {/* {inside} */}
      <Footer />
    </div>
  );
}

function PlaylistPageRedirect(): JSX.Element {
  const { userHandle, postCollectionId } = useParams();
  if (!userHandle || !postCollectionId) {
    return <div>Unexpected</div>;
  }
  return (
    <Navigate replace to={`/@${userHandle}/playlists/${postCollectionId}`} />
  );
}

function RedirectToPodcastGPT() {
  // Actually this code does not matter, because the / redirect is handled by the AWS Amplify console.
  // But I'm leaving this here just in case / for consistency.
  useEffect(() => {
    window.location.href = 'https://PodcastGPT.ai';
  }, []);

  return null;
}

function App() {
  return (
    <div className="App">
      <div id="WebPageContainer">
        {/* {_Header()} */}
        <Router>
          <ApolloProvider client={gqlClient}>
            <QueryClientProvider client={queryClient}>
              <Routes>
                <Route path="/oldestHome" element={<Main />} />
                <Route path="/oldHome" element={<HomePageForListeners />} />
                <Route path="/" element={<RedirectToPodcastGPT />} />
                <Route path="/creators" element={<HomePageForCreators />} />
                <Route
                  path="/demo"
                  element={<FakeablePasswordSplashScreen />}
                />
                <Route
                  path="/secretdemo1"
                  element={<YouTubeLikeDemo allPodcasts={sanjayPodcasts()} />}
                />
                <Route
                  path="/secretdemo2"
                  element={<YouTubeLikeDemo allPodcasts={lexDemoPodcasts()} />}
                />

                {/* Super hacky way to get short-name playlist link for Atai's PsyPhy playlist... */}
                <Route
                  path="/@atai/playlists/PsyPhy"
                  element={
                    <Navigate
                      replace
                      to="/@atai/playlists/e3a9ae4c-ff27-425f-a0b0-7361fc1687e0"
                    />
                  }
                />
                <Route
                  path="/Tawk/:postId"
                  element={<Navigate replace to="/@defaultHandle/:postId" />}
                />
                <Route
                  path="/@:userHandle/:postId"
                  element={_WithHeaderFooter(<ExpandedPostLoading />)}
                />
                <Route
                  path="/@:userHandle"
                  element={_WithHeaderFooter(<UserProfileLoading />)}
                />
                <Route
                  path="/@:userHandle/playlists/:postCollectionId"
                  element={_WithHeaderFooter(<ExpandedPlaylistLoading />)}
                />
                <Route
                  path="/Collection/@:userHandle/:postCollectionId"
                  element={<PlaylistPageRedirect />}
                />
                <Route
                  path="/terms-of-service"
                  element={_WithHeaderFooter(<TermsOfService />)}
                />
                <Route
                  path="/privacy-policy"
                  element={_WithHeaderFooter(<PrivacyPolicy />)}
                />
                <Route
                  path="/copyright-policy"
                  element={_WithHeaderFooter(<CopyrightPolicy />)}
                />
                <Route path="/sandbox" element={<Sandbox />} />
              </Routes>
            </QueryClientProvider>
          </ApolloProvider>
        </Router>
        {/* {_Footer()} */}
      </div>
    </div>
  );
}

export default App;
